import { Alert, AlertDescription } from "@/components/ui/alert";
import { COSTUME_CATALOG, readCostumeUnlocks, setCostumeUnlocked } from "@/lib/costume-unlocks";
import { HERO_NAMES, type CloudAllData } from "@/types/cloud-data";

export const PART_NAMES = ["Weapon", "Hair", "Suit", "Accessory"];
const SET_NAMES: Record<string, string> = {
  Croven: "Raven",
  School: "Academia",
  Maid: "Maid / Butler",
  OG_Claris: "Original Claris",
  OtherNoEffect: "Other",
  TimeSkip: "Time Skip",
  Summer: "Tropical",
};
type Costume = (typeof COSTUME_CATALOG)[number];

export const CHARACTER_GROUPS = [...Object.entries(HERO_NAMES).map(([id, name]) => ({ id: Number(id), name })),
  { id: 0, name: "Shared" }].map(character => {
    const costumes = COSTUME_CATALOG.filter(item => item.hero === character.id);
    const sets = [...new Set(costumes.map(item => item.set))].map(set => ({
      id: set,
      name: SET_NAMES[set] ?? (set || "Other"),
      costumes: costumes.filter(item => item.set === set),
    })).sort((a, b) => a.name.localeCompare(b.name));
    return { ...character, costumes, sets };
  }).filter(character => character.costumes.length > 0);

export function CostumesTab({ cloudData, onSaveCloudData, disabled = false }: {
  cloudData: CloudAllData | null;
  onSaveCloudData: (next: CloudAllData) => void;
  disabled?: boolean;
}) {
  if (!cloudData?.userData) return <Alert><AlertDescription>No cloud user data available. Use the Raw tab to inspect this save.</AlertDescription></Alert>;
  const unlocks = readCostumeUnlocks(cloudData);
  if (!unlocks) return <Alert variant="destructive"><AlertDescription>Costume ownership data is invalid. Inspect it in the Raw tab before editing costumes.</AlertDescription></Alert>;
  const owned = (id: string) => Object.prototype.hasOwnProperty.call(unlocks, id);
  const unlockedCount = COSTUME_CATALOG.filter(item => !item.alwaysUnlocked && owned(item.id)).length;
  const unlockableCount = COSTUME_CATALOG.filter(item => !item.alwaysUnlocked).length;

  function renderCostume(item: Costume) {
    return <label key={item.id} className="flex items-center gap-3 px-3 py-2 hover:bg-muted/30 cursor-pointer">
      <input type="checkbox" className="h-4 w-4 shrink-0 accent-primary"
        aria-label={`Unlock ${item.name} (${HERO_NAMES[item.hero] ?? "Shared"}, ${item.id})`}
        checked={item.alwaysUnlocked || owned(item.id)} disabled={disabled || item.alwaysUnlocked}
        onChange={event => onSaveCloudData(setCostumeUnlocked(cloudData!, item.id, event.target.checked))} />
      <span className="min-w-0 flex-1">
        <span className="block text-sm">{item.name}</span>
        <code className="block text-xs text-muted-foreground break-all">{item.id}</code>
      </span>
      <span className="text-xs text-muted-foreground text-right">
        {item.alwaysUnlocked ? "Always unlocked" : PART_NAMES[item.part] ?? item.part}
      </span>
    </label>;
  }

  return <div className="space-y-3">
    <div>
      <h3 className="text-sm font-medium">Unlocked costumes ({unlockedCount} / {unlockableCount})</h3>
      <p className="text-xs text-muted-foreground mt-1">Check costumes to unlock them, then use Save. The player receives the changes on their next cloud data load. Unchecking removes ownership. Default costumes are always available.</p>
    </div>
    <div className="space-y-2">
      {CHARACTER_GROUPS.map(character => <details key={character.id} className="rounded border border-border">
        <summary className="px-3 py-2 text-sm font-medium cursor-pointer hover:bg-muted/30">
          {character.name} <span className="text-xs text-muted-foreground font-normal">({character.costumes.length})</span>
        </summary>
        <div className="border-t border-border/50 pl-4 pr-2 py-2 space-y-1">
          {character.id === 0 ? character.costumes.map(renderCostume) : character.sets.map(set =>
            <details key={set.id} className="rounded border border-border/50">
              <summary className="px-3 py-2 text-sm cursor-pointer hover:bg-muted/30">
                {set.name} <span className="text-xs text-muted-foreground">({set.costumes.length})</span>
              </summary>
              <div className="border-t border-border/50 pl-4">{set.costumes.map(renderCostume)}</div>
            </details>)}
        </div>
      </details>)}
    </div>
  </div>;
}
